import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { ComponentProps, useId } from 'react';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { getNavigationDisplayName, NavigationEntityType } from '../../../../models/hooks/useAINavigationResolver';
import { useEventLogging } from '../../../../models/Logging';
import {
	getConversationChatMessageQueryKey,
	updateMessageInConversationCache,
} from '../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { useAIConversationMessageFeedbackMutation } from '../../../../queries/AIConversation/useAIConversationMessageFeedbackMutation';
import { useDeleteAIConversationMessageFeedbackMutation } from '../../../../queries/AIConversation/useDeleteAIConversationMessageFeedbackMutation';
import { ThumbsDownIcon } from '../../../components/svgs/icons/ThumbsDownIcon';
import { ThumbsUpIcon } from '../../../components/svgs/icons/ThumbsUpIcon';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/Tooltip';
import {
	AIChatAssistantMessage,
	AIChatAssistantMessageError,
	AIChatMessageError,
	AIChatUserMessage,
} from '../../../containers/templates/AssistantChat';
import { AssistantChatContactCard } from '../../../containers/templates/AssistantChat/AssistantChatContactCard';
import { CLIENT_MESSAGE_ID_PREFIX } from '../../../containers/templates/AssistantChat/utils';
import { darkGrayFontColor, white } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { Button } from '../../Button';
import { THEME } from '../../Theme/theme';
import { MediaContentDisplay } from '../../UnifiedImageDialog/AIImageChat';
import { AIMarkdownResponse } from '../AiMarkdownResponse';

interface PartialToolCall extends Api.AIToolCall {
	partial: Record<string, string>;
}

function isEmailToolCall(toolCall: Api.AIToolCall): toolCall is Api.EmailToolCall {
	return Boolean((toolCall as Api.EmailToolCall)?.body);
}

function isSocialToolCall(toolCall: Api.AIToolCall): toolCall is Api.SocialMediaToolCall {
	return Boolean((toolCall as Api.SocialMediaToolCall)?.content);
}

function isGoogleReviewReplyToolCall(toolCall: Api.AIToolCall): toolCall is Api.GoogleReviewReplyToolCall {
	return Boolean((toolCall as Api.GoogleReviewReplyToolCall)?.reply);
}

function isBlogToolCall(toolCall: Api.AIToolCall): toolCall is Api.BlogToolCall {
	return Boolean((toolCall as Api.BlogToolCall)?.title);
}

function isContactListToolCall(toolCall: Api.AIToolCall): toolCall is Api.ContactListToolCall {
	return Boolean((toolCall as Api.ContactListToolCall)?.toolName === 'lev-contacts');
}

function isNavigationToolCall(toolCall: Api.AIToolCall): toolCall is Api.NavigationToolCall {
	return Boolean((toolCall as Api.NavigationToolCall)?.toolName === 'lev-navigation');
}

function isMediaContentToolCall(toolCall: Api.AIToolCall): toolCall is Api.MediaContentToolCall {
	return toolCall.toolName === 'lev-media-content';
}

function emailToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { subject: string; body: string };
	if (isEmailToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function socialToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { content: string };
	if (isSocialToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

function googleReviewToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { reply: string };
	if (isGoogleReviewReplyToolCall(toolCall)) {
		content = { reply: toolCall.reply };
	} else if (toolCall.partial) {
		content = { reply: (toolCall.partial as { body: string }).body };
	}
	return content;
}

function blogToolCallPreviewContent(toolCall: PartialToolCall) {
	let content: { title: string; body: string };
	if (isBlogToolCall(toolCall)) {
		content = JSON.parse(toolCall.value);
	} else if (toolCall.partial) {
		content = toolCall.partial as typeof content;
	}
	return content;
}

// Message Action Components
export type MessageActionsProps = ComponentProps<'div'> & {
	styles?: any[];
};

export const MessageActions = ({ className, children, styles = [], ...props }: MessageActionsProps) => (
	<div className={css(s.messageActions, ...styles)} {...props}>
		{children}
	</div>
);

export type MessageActionProps = ComponentProps<typeof Button> & {
	tooltip?: string;
	label?: string;
};

export const MessageAction = ({
	tooltip,
	children,
	label,
	variant = 'ghost',
	size = 'sm',
	...props
}: MessageActionProps) => {
	const button = (
		<Button size={size} variant={variant} {...props}>
			{children}
			<span className={css(s.srOnly)}>{label || tooltip}</span>
		</Button>
	);

	if (tooltip) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger asChild>{button}</TooltipTrigger>
					<TooltipContent>
						<p className={css(s.tooltipText)}>{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}

	return button;
};

export type MessageToolbarProps = ComponentProps<'div'> & {
	styles?: any[];
};

export const MessageToolbar = ({ className, children, styles = [], ...props }: MessageToolbarProps) => (
	<div className={css(s.messageToolbar, ...styles)} {...props}>
		{children}
	</div>
);

interface Props {
	message: Api.AIChatMessage;
	conversationId: string;
	impersonationContext?: Api.IImpersonationContext;
	onUseAIEmail?: (
		subject: string,
		content: Api.IRawRichTextContentState,
		audienceInfo?: Api.ContactAudienceInfo,
		aiReference?: Api.IAIReference
	) => void;
	onUseContact?: (value: Api.AIContactReference) => void;
	onUseContactList?: (contactList: Api.ContactListToolCall) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onNavigate?: (entityType: string, entityId: string, parentEntityType?: string, parentEntityId?: string) => void;
	onErrorTryAgain?: (chatRequest: Api.AIChatRequest) => void;
	onUseImage?: (fileAttachments: Api.IFileAttachment[]) => void;
	onImageGenJobComplete?: () => void;
	showMessageToolbar?: boolean;
	onApproveContentCalendar?: (toolCall: Api.ContentCalendarResourceToolCall) => void;
	onApproveContentCalendarLoading?: boolean;
}

export function AIConversationMessage({
	message,
	conversationId,
	impersonationContext,
	onUseAIEmail,
	onUseContact,
	onUseContactList,
	onUseSocialContent,
	onUseGoogleReviewReply,
	onUseBlogContent,
	onNavigate,
	onErrorTryAgain,
	onUseImage,
	onImageGenJobComplete,
	showMessageToolbar = true,
	onApproveContentCalendar,
	onApproveContentCalendarLoading,
}: Props) {
	const id = useId();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('AIConversationMessage');
	const shouldShowActions =
		showMessageToolbar && message.role === Api.AIChatRole.Assistant && !message.id.startsWith(CLIENT_MESSAGE_ID_PREFIX);
	const feedbackMutation = useAIConversationMessageFeedbackMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('AIMessageFeedback-Error', error);
		},
	});
	const deleteFeedbackMutation = useDeleteAIConversationMessageFeedbackMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('AIMessageFeedbackDelete-Error', error);
		},
	});
	if (message.role === Api.AIChatRole.User) {
		return (
			<AIChatUserMessage>
				{message.text}
				{message.fileAttachments?.length ? (
					<div className={css(bs.flex, bs.gap2, bs.mt2, bs.flexWrap)}>
						{message.fileAttachments.map((attachment, idx) => (
							<img
								key={`${id}-attachment-${message.id}-${idx}`}
								src={attachment.url}
								alt={attachment.fileName}
								className={css(s.attachmentImage)}
							/>
						))}
					</div>
				) : null}
			</AIChatUserMessage>
		);
	}
	if (message.role === Api.AIChatRole.Assistant) {
		return (
			<AIChatAssistantMessage>
				{message.contents?.map((content, j) => {
					if (content._type === 'AITextContent') {
						const c = content as Api.AITextContent;
						return <AIMarkdownResponse key={`${id}-assistant-message-${j}`}>{c.text}</AIMarkdownResponse>;
					}
					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-email') {
						const partialEmailToolCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = emailToolCallPreviewContent(partialEmailToolCall);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent.subject ?? ''}</div>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.body ?? '',
									}}
								/>
								{isEmailToolCall(partialEmailToolCall) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => {
											onUseAIEmail?.(
												partialEmailToolCall.subject,
												partialEmailToolCall.body,
												partialEmailToolCall.audienceInfo,
												{ sessionId: conversationId }
											);
										}}
									>
										Use this email
									</Button>
								) : null}
							</div>
						);
					}
					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-contact') {
						const contactToolContent = (content as Api.AIToolContent).toolCall as unknown as Api.AIContactReference & {
							contactId: string;
						};
						return (
							<AssistantChatContactCard
								key={`contact-assistant-message${message?.id}-${j}`}
								firstName={contactToolContent.firstName}
								lastName={contactToolContent.lastName}
								email={contactToolContent.primaryEmail}
								phone={contactToolContent.phoneNumbers?.[0]?.value}
								company={contactToolContent.companyName}
								profilePic={contactToolContent.profilePic}
								onClick={() => {
									onUseContact?.({
										...contactToolContent,
										id: contactToolContent.contactId,
									});
								}}
							/>
						);
					}
					if (content._type === 'AIToolContent' && isContactListToolCall((content as Api.AIToolContent).toolCall)) {
						const contactListToolCall = (content as Api.AIToolContent).toolCall as Api.ContactListToolCall;
						return (
							<Button
								key={`contact-list-assistant-message${message?.id}-${j}`}
								onClick={() => {
									onUseContactList?.(contactListToolCall);
								}}
							>
								Show all {contactListToolCall.totalCount} contacts
							</Button>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-social-media-post'
					) {
						const partialSocialCall = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = socialToolCallPreviewContent(partialSocialCall);
						return (
							<div key={`social-assistant-message${message?.id}-${j}`} className={css(s.assistantEmail)}>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.content ?? '',
									}}
								/>
								{isSocialToolCall(partialSocialCall) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => onUseSocialContent?.(partialSocialCall.content, { sessionId: conversationId })}
									>
										Use this post
									</Button>
								) : null}
							</div>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-google-review-reply'
					) {
						const partialGoogleReviewReply = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = googleReviewToolCallPreviewContent(partialGoogleReviewReply);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent?.reply ?? ''}</div>
								{onUseGoogleReviewReply && isGoogleReviewReplyToolCall(partialGoogleReviewReply) ? (
									<Button
										styles={[s.useEmailBtn]}
										onClick={() => onUseGoogleReviewReply(partialGoogleReviewReply.reply, message.aiConversationId)}
									>
										Use this Response
									</Button>
								) : null}
							</div>
						);
					}
					if (
						content._type === 'AIToolContent' &&
						(content as Api.AIToolContent).toolCall.toolName === 'lev-resource'
					) {
						const toolCall = (content as Api.AIToolContent).toolCall as unknown as { id: string; resourceId: string };
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<Button
									variant='primary'
									styles={[s.useEmailBtn]}
									isLoading={Boolean(onApproveContentCalendarLoading)}
									disabled={Boolean(onApproveContentCalendarLoading)}
									onClick={() =>
										onApproveContentCalendar?.({
											id: toolCall.id,
											resourceId: toolCall.resourceId,
											calendarId: toolCall.resourceId,
										})
									}
								>
									Use this Content
								</Button>
							</div>
						);
					}

					if (content._type === 'AIToolContent' && (content as Api.AIToolContent).toolCall.toolName === 'lev-blog') {
						const partialBlog = (content as Api.AIToolContent).toolCall as PartialToolCall;
						const previewContent = blogToolCallPreviewContent(partialBlog);
						return (
							<div key={`${id}-assistant-message-${j}`} className={css(s.assistantEmail)}>
								<div>{previewContent?.title ?? ''}</div>
								<div
									dangerouslySetInnerHTML={{
										__html: previewContent?.body ?? '',
									}}
								/>
								{isBlogToolCall(partialBlog) ? (
									<Button
										variant='primary'
										styles={[s.useEmailBtn]}
										onClick={() =>
											onUseBlogContent?.(partialBlog.title, partialBlog.content, { sessionId: conversationId })
										}
									>
										Use this blog
									</Button>
								) : null}
							</div>
						);
					}
					if (content._type === 'AIToolContent' && isNavigationToolCall((content as Api.AIToolContent).toolCall)) {
						const navigationToolCall = (content as Api.AIToolContent).toolCall as Api.NavigationToolCall;

						return (
							<div key={`navigation-assistant-message${message?.id}-${j}`}>
								{navigationToolCall.navigationItems?.map((navigationItem, navIndex) => {
									const displayText = getNavigationDisplayName(navigationItem.entityType as NavigationEntityType);
									// Extract parent entity information from parentNavigationMetadata
									const parentEntityType = navigationItem.parentNavigationMetadata?.entityType;
									const parentEntityId = navigationItem.parentNavigationMetadata?.id;
									return (
										<Button
											key={`navigation-item-${message?.id}-${j}-${navIndex}`}
											onClick={() => {
												onNavigate?.(navigationItem.entityType, navigationItem.id, parentEntityType, parentEntityId);
											}}
											styles={[s.useEmailBtn]}
										>
											{displayText}
										</Button>
									);
								})}
								{(!navigationToolCall.navigationItems || navigationToolCall.navigationItems.length === 0) && (
									<div key={`navigation-empty-${message?.id}-${j}`}>No navigation items available</div>
								)}
							</div>
						);
					}

					if (content._type === 'AIToolContent' && isMediaContentToolCall((content as Api.AIToolContent).toolCall)) {
						const toolContent = content as Api.AIToolContent;
						return (
							<MediaContentDisplay
								key={`${id}-assistant-message-${j}`}
								toolCall={toolContent.toolCall}
								onJobComplete={onImageGenJobComplete}
								onUseImage={onUseImage}
							/>
						);
					}
				})}
				{shouldShowActions ? (
					<MessageToolbar>
						<MessageActions>
							<MessageAction
								tooltip={
									message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp ? 'Remove feedback' : 'Good response'
								}
								variant='ghost'
								styles={[message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp && s.messageActionToggled]}
								onClick={() => {
									if (feedbackMutation.isPending || deleteFeedbackMutation.isPending) return;
									if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsUp) {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												feedback: undefined,
											}
										);
										deleteFeedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											impersonationContext,
										});
									} else {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												// @ts-ignore
												feedback: {
													value: Api.AIChatMessageFeedback.ThumbsUp,
												},
											}
										);
										feedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											feedbackType: Api.AIChatMessageFeedback.ThumbsUp,
											impersonationContext,
										});
									}
								}}
							>
								<ThumbsUpIcon />
							</MessageAction>
							<MessageAction
								tooltip={
									message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown ? 'Remove feedback' : 'Bad response'
								}
								variant='ghost'
								styles={[message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown && s.messageActionToggled]}
								onClick={() => {
									if (feedbackMutation.isPending || deleteFeedbackMutation.isPending) return;
									if (message.feedback?.value === Api.AIChatMessageFeedback.ThumbsDown) {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												feedback: undefined,
											}
										);
										deleteFeedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											impersonationContext,
										});
									} else {
										// Optimistically update the message in the cache
										updateMessageInConversationCache(
											getConversationChatMessageQueryKey({ id: conversationId, impersonationContext }),
											{
												...message,
												// @ts-ignore
												feedback: {
													value: Api.AIChatMessageFeedback.ThumbsDown,
												},
											}
										);
										feedbackMutation.mutate({
											conversationId,
											messageId: message.id,
											feedbackType: Api.AIChatMessageFeedback.ThumbsDown,
											impersonationContext,
										});
									}
								}}
							>
								<ThumbsDownIcon />
							</MessageAction>
						</MessageActions>
					</MessageToolbar>
				) : undefined}
			</AIChatAssistantMessage>
		);
	}
	if ((message as AIChatMessageError).error) {
		const errorMessage = message as AIChatMessageError;
		return <AIChatAssistantMessageError onTryAgain={() => onErrorTryAgain?.(errorMessage.chatRequest)} />;
	}

	return null;
}

const s = StyleSheet.create({
	messageActionToggled: {
		color: darkGrayFontColor,
		':has(*) > svg': {
			fill: darkGrayFontColor,
		},
		':has(*) > svg > path:last-child': {
			color: white,
		},
	},
	useEmailBtn: {
		width: `100%`,
	},
	assistantEmail: {
		display: `flex`,
		flexDirection: `column`,
		gap: 16,
		fontWeight: `normal`,
	},
	attachmentImage: {
		maxWidth: 200,
		maxHeight: 200,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
		objectFit: 'contain',
	},
	messageActions: {
		display: 'flex',
		alignItems: 'center',
	},
	messageToolbar: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
	},
	tooltipText: {
		padding: 0,
		margin: 0,
	},
	srOnly: {
		position: 'absolute',
		width: 1,
		height: 1,
		padding: 0,
		margin: -1,
		overflow: 'hidden',
		clip: 'rect(0, 0, 0, 0)',
		whiteSpace: 'nowrap',
		borderWidth: 0,
	},
});
